<template>
  <div class="flex flex-col justify-center items-center gap-3 pb-3">
    <KeysLayout class="my-4" v-slot="s">
      <template v-for="(k, id) in s.keys" :key="id">
        <KeyCap
          ref="keycaps"
          class="keycap-no-transitions"
          :row="k.row"
          :col="k.col"
          :x="k.key.x"
          :y="k.key.y"
          :rot="k.key.rot"
          :height="k.key.size.y"
          :width="k.key.size.x"
          :blank="true"
          :outlined="selected.has(k.row, k.col)"
          @click="
            () => {
              blockNextSetColor();
              selected.toggle(k.row, k.col);
            }
          "
        />
      </template>
    </KeysLayout>

    <div class="flex justify-center items-center gap-3">
      <ColorDot
        v-for="(c, id) in colors.defaultColors"
        :key="id"
        :color="c.toHex()"
        @click="() => setColor(rgb8AsArray(c.toRgb()))"
      />

      <ColorInput
        :class="selected.count == 0 ? 'pointer-events-none opacity-40' : ''"
        :modelValue="currentColor"
        @update:modelValue="setColor"
      />

      <button class="btn btn-accent" @click="setRandom">Random</button>
    </div>

    <div class="flex justify-center items-center gap-6">
      <div class="flex justify-center items-center gap-1">
        <button class="btn btn-neutral" @click="setAll(() => true)">All</button>
        <button class="btn btn-neutral" @click="setAll(() => false)">
          Clear
        </button>
        <button
          class="btn btn-neutral"
          @click="setAll((k) => k.col < kb.columns / 2)"
        >
          Left
        </button>
        <button
          class="btn btn-neutral"
          @click="setAll((k) => k.col >= kb.columns / 2)"
        >
          Right
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { colord } from "colord";
import { computed, ref, nextTick } from "vue";
import { PairSet } from "@/utils";
import { RGB8, rgb8AsArray, rgb8AsDict } from "@/keyboard";
import {
  useKeycapColorsStore,
  useKeyboardStore,
  useLayoutStore,
} from "@/stores";
import KeysLayout from "@/components/utils/KeysLayout.vue";
import ColorDot from "@/components/utils/ColorDot.vue";
import ColorInput from "@/components/utils/ColorInput.vue";
import KeyCap from "@/components/keys/KeyCap.vue";

const colors = useKeycapColorsStore();
const layout = useLayoutStore();
const kb = useKeyboardStore();

const keycaps = ref<InstanceType<typeof KeyCap>[]>([]);
const selected = ref<PairSet>(new PairSet());

// Channing selected may change current color, which in turn sets this color for all selected
// items. It e.g. happens when clicking the selection buttons (all/left/right) or generating
// random colors. For this reason after such operations we need to block a single setColor.
let blockSetColor = false;
const blockNextSetColor = () => {
  blockSetColor = true;
  nextTick(() => nextTick(() => (blockSetColor = false)));
};

const firstSelected = computed(() => {
  const first = selected.value.iter()[0];
  if (first == undefined) return;
  return keycaps.value.find(
    (k) => k.$props.row == first[0] && k.$props.col == first[1]
  );
});

const currentColor = computed((): [number, number, number] => {
  const keycap = firstSelected.value;
  if (keycap == undefined) return [0, 0, 0];
  return rgb8AsArray(colors.at(keycap.$props.row, keycap.$props.col).toRgb());
});

const setColor = (rgb: RGB8) => {
  if (blockSetColor) {
    return;
  }
  for (const [row, col] of selected.value.iter()) {
    const keycap = keycaps.value.find(
      (k) => k.$props.row == row && k.$props.col == col
    );
    if (keycap != undefined) {
      colors.setKey(
        keycap.$props.row,
        keycap.$props.col,
        colord(rgb8AsDict(rgb))
      );
    }
  }
};

type Key = ReturnType<typeof layout.layout.iter>[0];
const setAll = (isSelected: (key: Key) => boolean) => {
  blockNextSetColor();
  layout.layout
    .iter()
    .forEach((key) =>
      isSelected(key)
        ? selected.value.add(key.row, key.col)
        : selected.value.remove(key.row, key.col)
    );
};

const setRandom = () => {
  blockNextSetColor();
  const selection = selected.value.iter();
  const at = selection.length > 0 ? selection : undefined;
  colors.setRandom(at);
};
</script>
